import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IScheduleInfo } from '../../../schedule/types/IScheduleInfo';
import { Set } from '../../../ts/models/Set';
import { UpdateScheduleRequest } from '../../../backgroundJob/types/UpdateScheduleRequest';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { IScheduleDetails } from '../../../schedule/types/ISchedule';
import { CreateScheduleAction } from '../../../ts/services/CreateScheduleAction';
import { ErrorNotifyingServer } from '../../../ts/services/ErrorNotifyingServer';
import { RunSchedulesAction } from '../../../ts/services/RunSchedulesAction';
import { RunServerSchedulesAction } from '../../../ts/services/RunServerSchedulesAction';
import { SetScheduleStatusAction } from '../../../ts/services/SetScheduleStatusAction';
import { ScheduleDisable, ScheduleEdit, ScheduleEnable, ScheduleRunNow } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { ScheduleMenuAction } from '../../../schedule/actions/ScheduleMenuActions';
import { AdminSelfServiceScheduleDialog, AdminSelfServiceScheduleDialogState } from '../../content/AdminSelfServiceScheduleDialog';
import { NavLocationSelectors } from '../../selectors/NavigationStateSelectors';
import { ServiceLocator } from '../../ServiceLocator';

export function runServerSchedules(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<RunServerSchedulesAction>('RunServerSchedulesAction')
    .execute(Set.fromIdItemArray(schedules), context);
}

export function runSiteSchedules(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<RunSchedulesAction>('RunSchedulesAction')
    .execute(Set.fromIdItemArray(schedules), context);
}

export function* handleRunNow(action: ScheduleMenuAction): SagaIterator {
  try {
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (inServerArea) {
      yield call(runServerSchedules, action.items);
    } else {
      yield call(runSiteSchedules, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function enable(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<SetScheduleStatusAction>('SetScheduleStatusAction')
    .confirmEnableSchedules(Set.fromIdItemArray(schedules), context);
}

export function* handleEnable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(enable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function disable(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<SetScheduleStatusAction>('SetScheduleStatusAction')
    .confirmDisableSchedules(Set.fromIdItemArray(schedules), context);
}

export function* handleDisable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(disable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editSelfServiceSchedule(schedule: IScheduleInfo) {
  const recurrence = IScheduleInfo.toSSSRecurrence(schedule);

  const save = (scheduleState: AdminSelfServiceScheduleDialogState) => {
    const server = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const request = new UpdateScheduleRequest({
      name: scheduleState.name,
      scheduleType: scheduleState.recurrence.recurrenceType,
      scheduleDetails: scheduleState.recurrence.recurrenceDetails,
      priority: scheduleState.priority,
      parallel: scheduleState.isParallel,
      id: schedule.id,
    });

    return new Promise<IResult>((resolve, reject) => {
      server.sendRequest(request).then(result => {
        CreateScheduleAction.notifyResult(result, schedule.name, scheduleState.name);
        return resolve(result);
      }).catch(err => reject(err));
    });
  };
  return AdminSelfServiceScheduleDialog.open(save, recurrence, schedule.name, schedule.scheduledAction, schedule.priority);
}

export function edit(schedules: IScheduleInfo[]) {
  if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
    const schedule = schedules[0];
    return editSelfServiceSchedule(schedule);
  } else {
    const context = ServiceLocator.getAngularActionContext();
    return ServiceLocator.get<CreateScheduleAction>('CreateScheduleAction')
      .editSchedule(schedules[0], context);
  }
}

export function* handleEdit(action: ScheduleMenuAction): SagaIterator {
  try {
    if (action.items.length > 0) {
      yield call(edit, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootScheduleMenuActionSaga(): SagaIterator {
  yield takeEvery(ScheduleRunNow, handleRunNow);
  yield takeEvery(ScheduleEnable, handleEnable);
  yield takeEvery(ScheduleDisable, handleDisable);
  yield takeEvery(ScheduleEdit, handleEdit);
}
